import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from './api';

export interface User {
  id: string;
  email: string;
  fullName?: string;
  role: string;
  isPlatformOwner?: boolean;
}

export interface Website {
  id: string;
  domain: string;
  name: string;
  isPrimary: boolean;
  isActive: boolean;
  description?: string;
  favicon?: string;
  googleAnalyticsId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: string;
  onboardingCompleted: boolean;
  walletBalance?: number; // Token balance
  websites?: Website[]; // Multi-domain support
  primaryWebsiteId?: string | null;
  activeWebsiteId?: string | null;
  settings?: {
    onboarding?: {
      companyName?: string | null;
      website?: string | null;
      industry?: string | null;
      description?: string | null;
      country?: string | null;
      phone?: string | null;
      contentSources?: {
        blogUrls?: string[];
        productUrls?: string[];
      };
      selectedPlatforms?: string[];
      onboardingCompleted?: boolean;
    };
  };
}

interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  workspaceName: string;
}

interface LoginResponse {
  user?: User;
  workspace?: Workspace;
  requireTwoFactor?: boolean;
  requireDeviceVerification?: boolean;
}

interface AuthState {
  user: User | null;
  workspace: Workspace | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResponse | undefined>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setWorkspace: (workspace: Workspace | null) => void;
  updateWalletBalance: (balance: number) => void;
  setActiveWebsite: (websiteId: string) => Promise<void>;
  refreshWorkspace: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      workspace: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string, rememberMe: boolean = false) => {
        set({ isLoading: true });
        try {
          console.log('[AUTH] Starting login for:', email, 'rememberMe:', rememberMe);
          const response = await apiClient.post('/auth/login', {
            email,
            password,
            rememberMe,
          });

          console.log('[AUTH] Raw API response:', response.data);
          const data = response.data.data || response.data;
          console.log('[AUTH] Extracted data:', data);

          // Check if device verification or 2FA is required
          if (data.requireDeviceVerification || data.requireTwoFactor) {
            console.log('[AUTH] 2FA/Device verification required');
            set({ isLoading: false });
            return {
              requireDeviceVerification: data.requireDeviceVerification,
              requireTwoFactor: data.requireTwoFactor,
              user: data.user,
              workspace: data.workspace,
            };
          }

          // Normal login success
          const { user, workspace } = data;
          console.log('[AUTH] Setting auth state:', { user, workspace });
          console.log('[AUTH] Wallet balance from login:', workspace?.walletBalance);
          set({
            user,
            workspace,
            isAuthenticated: true,
            isLoading: false,
          });

          console.log('[AUTH] Auth state updated successfully');
          return { user, workspace };
        } catch (error) {
          console.error('[AUTH] Login failed:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          console.log('Starting registration with:', data.email);
          const response = await apiClient.post('/auth/register', data);

          console.log('Registration API response:', response.data);
          const { data: { user, workspace } } = response.data;

          console.log('Setting auth state:', { user, workspace, isAuthenticated: true });
          set({
            user,
            workspace,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error('Registration failed:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await apiClient.post('/auth/logout');
        } finally {
          set({
            user: null,
            workspace: null,
            isAuthenticated: false,
          });
        }
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      setWorkspace: (workspace: Workspace | null) => {
        set({ workspace });
      },

      updateWalletBalance: (balance: number) => {
        set((state) => ({
          workspace: state.workspace
            ? { ...state.workspace, walletBalance: balance }
            : null,
        }));
      },

      setActiveWebsite: async (websiteId: string) => {
        try {
          console.log('[AUTH] Switching active website to:', websiteId);
          const response = await apiClient.put('/websites/workspace/active-website', { websiteId });

          const data = response.data.data || response.data;
          console.log('[AUTH] Active website switched:', data);

          // Update workspace with new activeWebsiteId and updated websites array
          set((state) => {
            if (!state.workspace) return state;

            const updatedWebsites = state.workspace.websites?.map(ws => ({
              ...ws,
              isActive: ws.id === websiteId,
            }));

            return {
              workspace: {
                ...state.workspace,
                activeWebsiteId: websiteId,
                websites: updatedWebsites,
              },
            };
          });
        } catch (error) {
          console.error('[AUTH] Failed to switch active website:', error);
          throw error;
        }
      },

      refreshWorkspace: async () => {
        try {
          console.log('[AUTH] Refreshing workspace data...');
          const response = await apiClient.get('/auth/me');

          if (response.data.user && response.data.workspace) {
            console.log('[AUTH] Workspace refreshed successfully:', response.data.workspace);
            set({
              user: response.data.user,
              workspace: response.data.workspace,
              isAuthenticated: true,
            });
          }
        } catch (error) {
          console.error('[AUTH] Failed to refresh workspace:', error);
          // Don't throw - this is a background refresh
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        workspace: state.workspace,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
